import axios from 'axios';
import { decodeJwt } from 'jose';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

// JWT validation helper (exported for reuse)
export const isJwtExpired = (token) => {
  if (!token) {
    return true;
  }
  try {
    const payload = decodeJwt(token);
    const isExpired = Date.now() >= payload.exp * 1000;
    return isExpired;
  } catch {
    return true;
  }
};

const useAuthStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // State
        accessToken: null,
        user: null,
        businesses: [],
        selectedBusiness: null,
        timezone: null,
        next: null,
        isAuthLoading: true,

        // Actions
        setAccessToken: (accessToken) => set({ accessToken }),

        setUser: (user) => set({ user }),

        patchUser: (patch) =>
          set((state) => ({
            user: state.user ? { ...state.user, ...patch } : null,
          })),

        setBusinesses: (businesses) => set({ businesses }),

        setSelectedBusiness: (selectedBusiness) => set({ selectedBusiness }),

        setTimezone: (timezone) => set({ timezone }),

        setNext: (next) => set({ next }),

        setIsAuthLoading: (isAuthLoading) => set({ isAuthLoading }),

        clearAuthData: () =>
          set({
            accessToken: null,
            user: null,
            businesses: [],
            selectedBusiness: null,
          }),

        logout: async (navigate) => {
          try {
            await axios.post('/auth/logout');
          } catch {
            // Continue with local logout even if API call fails
          } finally {
            // Always clear local auth data
            get().clearAuthData();
            if (navigate) {
              navigate('/login');
            } else {
              // Fallback for when navigate is not available
              window.location.href = '/login';
            }
          }
        },

        can: (requiredRole) => {
          const { selectedBusiness } = get();
          if (!selectedBusiness?.role) return false;
          const priority = { viewer: 0, member: 1, admin: 2, owner: 3 };
          return priority[selectedBusiness.role] >= priority[requiredRole];
        },

        // Async actions - these will be called from React components
        initializeTimezone: () => {
          const { timezone } = get();
          if (!timezone) {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            get().setTimezone(userTimezone);
          }
        },

        fetchUserData: async (axios, navigate, location, skipEmailRedirect = false, skipLoading = false) => {
          const { accessToken, setUser, setBusinesses, clearAuthData, setIsAuthLoading } = get();

          if (!accessToken) {
            return;
          }

          if (!skipLoading) {
            setIsAuthLoading(true);
          }

          try {
            const res = await axios.get('/users/me');

            const data = res.data.data;
            const newBusinesses = data.businesses;

            setUser(data);
            setBusinesses(newBusinesses);

            // Update selected business
            const { selectedBusiness } = get();
            if (!selectedBusiness) {
              set({ selectedBusiness: newBusinesses[0] || null });
            } else {
              const updated = newBusinesses.find((b) => b.businessId === selectedBusiness.businessId);
              set({ selectedBusiness: updated || null });
            }

            // PostHog identification
            const selectedBiz = newBusinesses[0] || null;
            window.posthog?.identify(data.id, {
              email: data.email,
              name: `${data.firstName} ${data.lastName}`,
              business_id: selectedBiz?.businessId,
              business_role: selectedBiz?.role,
              business_name: selectedBiz?.businessName,
            });

            // Group identification for business-level analytics
            if (selectedBiz?.businessId) {
              window.posthog?.group('business', selectedBiz.businessId, {
                name: selectedBiz.businessName,
                role: selectedBiz.role,
              });
            }

            // Redirect if email not verified (only on auth check, not initial login)
            if (!skipEmailRedirect && !data.emailVerifiedAt && location?.pathname !== '/verify-email') {
              navigate?.('/verify-email');
            }
          } catch (error) {
            // Only clear auth data on authentication failures (401), not on other server errors
            if (error.response?.status === 401) {
              clearAuthData();
            }
          } finally {
            if (!skipLoading) {
              setIsAuthLoading(false);
            }
          }
        },

        refreshUserData: async (axios, navigate, location) => {
          const { accessToken, fetchUserData, setIsAuthLoading } = get();
          if (!accessToken) {
            setIsAuthLoading(false);
            return Promise.resolve();
          }
          setIsAuthLoading(true);
          return fetchUserData(axios, navigate, location).finally(() => setIsAuthLoading(false));
        },

        checkAuthAndFetch: async (axios, navigate, location, skipEmailRedirect = false, skipLoading = false) => {
          const { accessToken, setIsAuthLoading, fetchUserData } = get();

          if (accessToken) {
            if (!skipLoading) {
              setIsAuthLoading(true);
            }
            await fetchUserData(axios, navigate, location, skipEmailRedirect, skipLoading);
          } else {
            setIsAuthLoading(false);
          }
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          accessToken: state.accessToken,
          user: state.user,
          businesses: state.businesses,
          selectedBusiness: state.selectedBusiness,
          timezone: state.timezone,
          next: state.next,
          isAuthLoading: state.isAuthLoading,
        }),
        onRehydrateStorage: () => (state) => {
          // Don't clear auth data on expired token - let the axios interceptor handle refresh reactively
          // This prevents the race condition where we clear data before attempting refresh

          // Mark rehydration complete
          if (state) {
            state._isHydrated = true;
          }
        },
      },
    ),
  ),
);

// Hydration tracking - Zustand persist doesn't expose this directly
// We'll use a separate property that gets set after rehydration
export const useAuthHydrated = () => {
  return useAuthStore((state) => state._isHydrated ?? false);
};

export default useAuthStore;
